import type { Report, ReportFinding, ReportFindingActionType } from "../types/reports";

/** Product disclaimer for TES-generated overview content. */
export const REPORT_FINDINGS_DISCLAIMER =
  "Summaries are based on your historical trading data and suggest testable hypotheses for your own review. This is not investment advice.";

export const FINDING_TEXT_MAX_CHARS = 1200;
export const NARRATIVE_MARKDOWN_MAX_CHARS = 4000;

export type ReportFindingDisplay = {
  id: string;
  title: string;
  action_type?: ReportFindingActionType | string;
  hypothesis: string;
  risk: string;
  validation: string;
  analysis: string;
  evidence_pointers: string[];
  /** Stable TES segment id for trade-management evidence (not explorer sort order). */
  primary_slice_id?: string;
  confidence?: number;
};

function trimJoin(parts: Array<string | undefined>): string {
  return parts
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join("\n\n");
}

export function truncateFindingText(text: string, maxLen = FINDING_TEXT_MAX_CHARS): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

export function formatFindingConfidence(value: number | undefined | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (value >= 0 && value <= 1) return `${Math.round(value * 100)}%`;
  return String(Math.round(value * 100) / 100);
}

/** True when stored finding matches TES v2 (or report is stamped v2). */
export function isFindingSchemaV2(
  finding: ReportFinding,
  reportSchemaVersion?: number
): boolean {
  if (reportSchemaVersion === 2) return true;
  if (reportSchemaVersion === 1) return false;
  if (typeof finding.validation === "string" && finding.validation.trim()) return true;
  if (typeof finding.analysis === "string" && finding.analysis.trim()) return true;
  const hasLegacy =
    Boolean(finding.assumption?.trim()) ||
    Boolean(finding.rule?.trim()) ||
    Boolean(finding.validation_plan?.trim()) ||
    Boolean(finding.expected_impact?.trim()) ||
    Boolean(finding.data_basis?.trim()) ||
    Boolean(finding.critical_analysis?.trim());
  if (hasLegacy) return false;
  if (
    finding.hypothesis?.trim() &&
    (finding.risk?.trim() || finding.validation?.trim() || finding.analysis?.trim())
  ) {
    return true;
  }
  return false;
}

export function getFindingSchemaVersion(report: Report | null | undefined): number | undefined {
  if (!report) return undefined;
  const top = (report as Report & { finding_schema_version?: number }).finding_schema_version;
  if (typeof top === "number" && Number.isFinite(top)) return top;

  const snap = report.input_snapshot;
  if (snap && typeof snap === "object" && !Array.isArray(snap)) {
    const v = (snap as Record<string, unknown>).finding_schema_version;
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }

  const first = Array.isArray(report.findings) ? report.findings[0] : null;
  if (first && typeof first === "object") {
    if (isFindingSchemaV2(first as ReportFinding)) return 2;
    const legacy = first as ReportFinding;
    if (
      legacy.assumption ||
      legacy.rule ||
      legacy.validation_plan ||
      legacy.expected_impact ||
      legacy.data_basis ||
      legacy.critical_analysis
    ) {
      return 1;
    }
  }
  return undefined;
}

/** Map Aura v1 keys into v2 display sections for legacy stored reports. */
export function normalizeFindingForDisplay(
  raw: ReportFinding,
  reportSchemaVersion?: number
): ReportFindingDisplay {
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : "";
  const title =
    typeof raw.title === "string" && raw.title.trim()
      ? raw.title.trim()
      : "Pattern observed";

  const pointers = Array.isArray(raw.evidence_pointers)
    ? raw.evidence_pointers.filter((p): p is string => typeof p === "string").slice(0, 8)
    : [];

  const primarySliceId =
    typeof raw.primary_slice_id === "string" && raw.primary_slice_id.trim()
      ? raw.primary_slice_id.trim()
      : undefined;

  if (isFindingSchemaV2(raw, reportSchemaVersion)) {
    return {
      id: id || "finding",
      title,
      action_type: raw.action_type,
      hypothesis: truncateFindingText(raw.hypothesis ?? ""),
      risk: truncateFindingText(raw.risk ?? ""),
      validation: truncateFindingText(raw.validation ?? ""),
      analysis: truncateFindingText(raw.analysis ?? ""),
      evidence_pointers: pointers,
      primary_slice_id: primarySliceId || undefined,
      confidence: raw.confidence,
    };
  }

  return {
    id: id || "finding",
    title,
    action_type: raw.action_type,
    hypothesis: truncateFindingText(
      trimJoin([raw.assumption, raw.hypothesis, raw.rule])
    ),
    risk: truncateFindingText(trimJoin([raw.risk, raw.expected_impact])),
    validation: truncateFindingText(
      trimJoin([raw.validation_plan, raw.invalidation, raw.validation])
    ),
    analysis: truncateFindingText(trimJoin([raw.data_basis, raw.critical_analysis])),
    evidence_pointers: pointers,
    primary_slice_id: primarySliceId || undefined,
    confidence: raw.confidence,
  };
}

export function sortFindingsByConfidence<T extends { confidence?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const ca = typeof a.confidence === "number" && Number.isFinite(a.confidence) ? a.confidence : -1;
    const cb = typeof b.confidence === "number" && Number.isFinite(b.confidence) ? b.confidence : -1;
    return cb - ca;
  });
}

export function findingHasDisplayContent(f: ReportFindingDisplay): boolean {
  return Boolean(
    f.title ||
      f.hypothesis ||
      f.risk ||
      f.validation ||
      f.analysis ||
      f.evidence_pointers.length > 0
  );
}

export function parseReportFindings(report: Report | null | undefined): ReportFindingDisplay[] {
  if (!report) return [];
  const snapshot = report.input_snapshot;
  const snapshotFindings =
    snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)
      ? (snapshot as Record<string, unknown>).findings
      : undefined;
  const findingsSource = Array.isArray(report.findings)
    ? report.findings
    : Array.isArray(snapshotFindings)
      ? (snapshotFindings as unknown[])
      : [];
  if (!findingsSource.length) return [];
  const schemaVersion = getFindingSchemaVersion(report);
  const parsed = findingsSource
    .filter((item): item is ReportFinding => Boolean(item) && typeof item === "object")
    .map((item) => normalizeFindingForDisplay(item, schemaVersion))
    .filter(findingHasDisplayContent);
  return sortFindingsByConfidence(parsed).slice(0, 3);
}

function readDiagnosticsObject(report: Report | null | undefined): Record<string, unknown> | null {
  if (!report) return null;
  const candidates: unknown[] = [
    (report as Report & { diagnostics?: unknown }).diagnostics,
    report.input_snapshot &&
    typeof report.input_snapshot === "object" &&
    !Array.isArray(report.input_snapshot)
      ? (report.input_snapshot as Record<string, unknown>).diagnostics
      : null,
    report.input_snapshot &&
    typeof report.input_snapshot === "object" &&
    !Array.isArray(report.input_snapshot)
      ? (report.input_snapshot as Record<string, unknown>).tes_diagnostics
      : null,
  ];
  for (const c of candidates) {
    if (c && typeof c === "object" && !Array.isArray(c)) {
      return c as Record<string, unknown>;
    }
  }
  return null;
}

/** TES returned HTTP 200 but no strict findings — treat as failed generation for UI. */
export function reportHasInsufficientTesOutput(report: Report | null | undefined): boolean {
  if (!report) return false;
  const snapshot = report.input_snapshot;
  const snapshotFindings =
    snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)
      ? (snapshot as Record<string, unknown>).findings
      : undefined;
  const findings = Array.isArray(report.findings)
    ? report.findings
    : Array.isArray(snapshotFindings)
      ? snapshotFindings
      : [];
  if (findings.length > 0) return false;
  const diag = readDiagnosticsObject(report);
  return diag?.data_quality === "insufficient_for_strict_output";
}

export function getReportTesDiagnosticsError(report: Report | null | undefined): string | null {
  const diag = readDiagnosticsObject(report);
  if (!diag) return null;
  const err = diag.error;
  return typeof err === "string" && err.trim() ? err.trim() : null;
}
